import type { ReactNode } from 'react'
import { Navigate } from 'react-router'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import type { Permission } from '@/constants/permissions'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/providers/authContext'
import { paths } from '@/router/paths'

export interface ProtectedRouteProps {
  /** Route requires this permission. Client-side gate for UX; backend enforces. */
  readonly permission?: Permission
  /** Any one of these is enough. */
  readonly anyPermission?: readonly Permission[]
  /** Superadmins only, whatever the role grants (Global Configuration). */
  readonly superadminOnly?: boolean
  readonly children: ReactNode
}

/**
 * The session is probably still good, but we could not confirm it. Composed
 * from `EmptyState` rather than styled here — no design exists for this state,
 * so it reuses the app's neutral one (see CLAUDE.md §11, §13).
 */
function SessionRestoreFailed({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <EmptyState
      className="min-h-svh"
      title="We Could Not Restore Your Session"
      description="The server did not answer our check. Your sign-in has not expired — try again in a moment."
      action={<Button onClick={onRetry}>Try Again</Button>}
    />
  )
}

/**
 * Unauthenticated -> /login (the next sign-in always lands on the dashboard).
 * Authenticated but lacking the permission -> /403.
 */
export function ProtectedRoute({
  permission,
  anyPermission,
  superadminOnly = false,
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, isRestoring, restoreFailed, refresh } = useAuth()
  const { can, canAny, isSuperadmin } = usePermission()

  // Still exchanging the stored token for a session — show the shell skeleton
  // rather than bouncing a logged-in user to /login on a hard reload.
  if (isRestoring) {
    return (
      <div className="p-6">
        <Skeleton variant="form" rows={4} label="Restoring session" />
      </div>
    )
  }

  // The stored session could not be restored, but it was never refused either —
  // the refresh call was rate limited, errored, or never reached the server. The
  // refresh token is still there, so this offers a retry instead of dropping the
  // user at /login with no explanation for a session that had hours left.
  if (restoreFailed) {
    return <SessionRestoreFailed onRetry={() => void refresh()} />
  }

  if (!isAuthenticated) {
    return <Navigate to={paths.login} replace />
  }

  const allowed =
    (!superadminOnly || isSuperadmin) &&
    (!permission || can(permission)) &&
    (!anyPermission?.length || canAny(anyPermission))

  if (!allowed) return <Navigate to={paths.forbidden} replace />

  return <>{children}</>
}
